import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Popover,
  Snackbar,
  Alert,
  Skeleton,
} from "@mui/material";
import DownloadIcon from "@/icons/Download";
import ChatIcon from "@/icons/ChatIcon";
import SuitcaseIcon from "@/icons/Suitcase";
import { Clock } from "@/icons/Clock";
import {
  useCancelInviteMutation,
  useCheckLimitMutation,
  useFollowUpMutation,
  useLazyGetAssessmentDetailsQuery,
  useLazyGetAssessmentNamesQuery,
  useReportSummaryMutation,
} from "@/redux/apis/manage-folder";
import { getDataFromURL, getFolderIdFromURL } from "@/utils/getFolderId";
import DotsMenuIcon from "@/icons/DotsMenuIcon";
import { formatDateWithoutTime } from "@/utils/index";
import styles from './styles.module.css'
import { getUserDetails } from "@/utils/getUserDetails";
import { useNavigate } from "react-router-dom";
import ArrowRight from "@/icons/ArrowRight";

type Props = {
  assessmentCampaignId?: string | null;
  setAssessmentId?: any;
  assessmentList: any[];
  setAssessmentList: Function;
  followUpCount: number;
};

const HeaderSection: React.FC<Props> = ({
  assessmentCampaignId,
  setAssessmentId = () => {},
  assessmentList,
  setAssessmentList = () => {},
  followUpCount,
}) => {
  const [selectedAssessment, setSelectedAssessment] = useState<any>(null);
  const [assessmentDetails, setAssessmentDetails] = useState<any>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [isToastOpen, setIsToastOpen] = useState(false);
  const [isToastSuccess, setIsToastSuccess] = useState(true);
  const [toastText, setToastText] = useState('');

  const [triggerCheckLimit, { data: checkLimitData ,isSuccess: checkLimitSuccess, error: checkLimitError, isLoading: checkLimitLoading }] = useCheckLimitMutation();
  const [triggerReportSummary, {isLoading: reportLoading}]= useReportSummaryMutation();
  const [
    triggerCancelAssessment,
    { data: cancelAssessmentResponse, isLoading: cancelAssessmentLoading, isSuccess: cancelSuccess, isError: cancelError },
  ] = useCancelInviteMutation();

  const [
    triggerFollowUp,
    { data: followUpResponse, isLoading: followUpLoading, isSuccess: followUpSuccess, isError: followUpError },
  ] = useFollowUpMutation();

  const [
    triggerGetAssessmentNames,
    { data: assessmentNamesData, isLoading: namesLoading },
  ] = useLazyGetAssessmentNamesQuery();
  const [
    triggerGetAssessmentDetails,
    { data: assessmentDetailsData, isLoading: detailsLoading },
  ] = useLazyGetAssessmentDetailsQuery();

  const userDetails = getUserDetails();
  const assessmentCampaignIdFromUrl = getDataFromURL('assessmentCampaignId');

  const navigate = useNavigate();
  const setQueryParams = (key: string, value: string) => {
    const params = new URLSearchParams(window.location.search);
    params.set(key, value); 
    navigate(`?${params.toString()}`); // Navigate to the updated URL
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const popoverData = [
    {
      label: 'Follow-up',
      count: followUpCount,
      action: 'followUp',
    },
    {
      label: 'Cancel Invite',
      count: followUpCount,
      action: 'cancel',
    },
  ]

  const handlePopoverClick = async (data: any) => {
    if (data?.count > 0) {

      if (data?.action === 'cancel') {
        const response = await triggerCancelAssessment({
          assessmentCampaignId: selectedAssessment.assessmentCampaignId!,
          assessmentProfileDetailsId: null,
          status: "cancel",
          type: "bulk",
        }).unwrap();

        if (response?.success) {
          setIsToastSuccess(true);
          setToastText('Your cancellation request is being processed. Please check back in a few minutes.');
          setIsToastOpen(true);
          handleClose();
        } else {
          setIsToastSuccess(false);
          setToastText('Something went wrong');
          setIsToastOpen(true);
        }

      }

      if (data?.action === 'followUp') {
        const response = await triggerFollowUp({
          assessmentCampaignId: selectedAssessment?.assessmentCampaignId!,
          assessmentProfileDetailsId: null,
          status: "followup",
          type: "bulk",
        }).unwrap();


        if (response?.success) {
          setIsToastSuccess(true);
          setToastText('Your followup request is being processed. Please check back in a few minutes.');
          setIsToastOpen(true);
          handleClose();
        } else {
          setIsToastSuccess(false);
          setToastText('Something went wrong');
          setIsToastOpen(true);
        }

      }

    }
  }

  const handleReportSummary = async() => {
    if(reportLoading) return;
    const response = await triggerReportSummary({
      campaignId: selectedAssessment?.assessmentCampaignId!,
      folderId: getFolderIdFromURL(),
    }).unwrap();
    window?.open(response);
  }

  useEffect(() => {
    const folderId = getFolderIdFromURL();
    triggerGetAssessmentNames({ folderId });
  }, [triggerGetAssessmentNames]);

  useEffect(() => {
    if (assessmentNamesData) {
      setAssessmentList(assessmentNamesData);
      if(assessmentCampaignIdFromUrl !== "undefined" && assessmentCampaignIdFromUrl !== "null"){
        const selectedItem = assessmentNamesData?.find(
          (item: any) => item?.assessmentCampaignId === Number(assessmentCampaignIdFromUrl)
        );
        setSelectedAssessment(selectedItem);
    }else{
      setSelectedAssessment(assessmentNamesData[0]); // Set the first assessment as default
    }
    }
  }, [assessmentNamesData]);

  useEffect(() => {
    if (selectedAssessment) {
      // setting id into url
      setQueryParams('assessmentCampaignId', selectedAssessment.assessmentCampaignId!);
      
      // api call
      triggerGetAssessmentDetails({
        positionId: selectedAssessment.positionId,
      });
      setAssessmentId(selectedAssessment.assessmentCampaignId!);

      triggerCheckLimit(
        {
           inventoryRequestObject : {
               corp_id : userDetails?.corp_id,
               recruiter_id : userDetails?.recruiter_id,
               channel_id : userDetails?.channel_id,
               subChannel_id : userDetails?.subhchannel_id,
               type : "AS"
          },
           folderId: getFolderIdFromURL(),
           positionId : selectedAssessment?.positionId,
           assessmentCampaignId : selectedAssessment?.assessmentCampaignId!,
      }
      );
    }
  }, [selectedAssessment, triggerGetAssessmentDetails]);

  useEffect(() => {
    if (assessmentDetailsData) {
      setAssessmentDetails(assessmentDetailsData[0]); // Assuming API returns an array
    }
  }, [assessmentDetailsData]);

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#FAFBFC",
        borderRadius: "8px 8px 0 0",
        padding: "16px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        borderBottom: "1px solid #cecece",
      }}
    >
      {/* Top Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "700",
              fontSize: "16px",
              color: "#17142A",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {selectedAssessment?.displayAssessmentName?.length > 0 && 
            <Box sx={{position: 'relative'}}>
              <TextField
              select
              value={selectedAssessment?.displayAssessmentName}
              onChange={(e) => {
                const selected = assessmentList.find(
                  (item) => item.displayAssessmentName === e.target.value
                );
                setSelectedAssessment(selected);
              }}
              SelectProps={{
                onOpen: () => { }, // Attach onOpen to SelectProps
                onClose: () => { }, // Attach onClose to SelectProps
                renderValue: (selected) => {
                  const selectedAssessment = assessmentList.find(
                    (item) => item.displayAssessmentName === selected
                  );
                  return (
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      <span className={styles.display_name}>{selectedAssessment?.displayAssessmentName}</span>
                      {assessmentDetails?.testType?.toLowerCase() === "custom" ? <span className={styles.span_style}>
                        {assessmentDetails?.testType}
                      </span>: null}
                    </span>
                  );
                },
                MenuProps: {
                  PaperProps: {
                    sx: {
                      maxHeight: "250px",
                      overflow: 'auto',
                    },
                  },
                  MenuListProps: {
                    sx: {
                      padding: 0,
                    },
                  },
                },
              }}
              sx={{
                maxWidth: "220px",
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                  border: "none",
                  boxShadow: "none",
                  paddingTop: "8px",
                  paddingBottom: "8px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiSelect-select": {
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#1D1934",
                  padding: '4px 14px 0 4px !important',
                },
                "& .MuiSelect-icon": {
                  display: "none !important",
                },
              }}
            >
              {assessmentList.map((assessment) => (
                <MenuItem
                  key={assessment.assessmentCampaignId}
                  value={assessment.displayAssessmentName}
                  sx={{
                    fontSize: "14px",
                    "&:hover": {
                      backgroundColor: "#ececec",
                    },
                    "&.Mui-selected": {
                      backgroundColor: "#f5e5ff",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "#f5e5ff",
                    },
                  }}
                >
                  {assessment.displayAssessmentName}
                </MenuItem>

              ))}
            </TextField>
          <div className={styles.filter_down_icon_title}><ArrowRight/></div>
            </Box>
            }

          </Typography>
        </Box>

        {/* Report Button Section */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon isDisabled={false} color= "#6a1b9a"/>}
            onClick={handleReportSummary}
            sx={{
              width: '160px',
              color: "#6a1b9a",
              borderColor: "#6a1b9a",
              textTransform: "none",
              fontWeight: "600",
              fontSize: "13px",
              borderRadius: "4px",
              padding: "8px 12px",
              cursor: !reportLoading ? 'pointer' : 'not-allowed',
              "&:hover": {
                backgroundColor: "#f4e4ff",
                color: 'rgb(106, 27, 154)',
              },
            }}
          >
            {!reportLoading ? "Report Summary" : "Loading..."}
          </Button>
          
            <IconButton
        id="dots-menu-icon-section"
        aria-describedby={id}
        size="small"
        onClick={handleClick}
        sx={{ padding: "8px 16px", 
            borderRadius: "4px",
            "&:hover": {
                background: "#E8E8EB ",
              },
         }}
      >
        <DotsMenuIcon />
      </IconButton>


          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Typography sx={{ p: 0 }}>
              {popoverData?.map(data => <Box
                onClick={()=>handlePopoverClick(data)}
                 sx={{ 
                  fontSize: '14px', 
                  padding: '10px 8px',
                  cursor: data?.count>0 ? 'pointer' : 'not-allowed',
                  color: data?.count>0 ? 'black' : 'gray',
                  "&:hover": {
                    background: "#f1f1f1",
              },
                  }}>
                {data.label} ({data.count})
              </Box>)}
            </Typography>
          </Popover>
        </Box>
      </Box>


       {/* Middle Section */}
     {detailsLoading || namesLoading ? 
     <Skeleton variant="rounded" width='100%' height={96} />
     : <>
       {selectedAssessment && (
        <>
          
          <Typography
        variant="body2"
        sx={{
          fontFamily: "Noto Sans", 
          fontSize: "12px",        
          fontWeight: 400,         
          lineHeight: "16px",      
          color: "#777584",     
          marginTop: '-18px',   
        }}
      >
        {formatDateWithoutTime(selectedAssessment?.createdAt)} | {checkLimitData?.totalAssessmentCampaignInvConsumed || 0} credits consumed
      </Typography>
      
          {/* Details Strip */}
          <Box
            sx={{
              backgroundColor: "#F6F6F7",
              borderRadius: "4px",
              padding: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: "12px",
              fontSize: "12px",
              fontWeight: 500,
              color: "#17142A",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SuitcaseIcon />
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                }}
              >
                {assessmentDetails?.difficultyLevel}
              </Typography>
            </Box>
            |
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ChatIcon />
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                }}
              >
                {assessmentDetails?.questionCount} questions
              </Typography>
            </Box>
            |
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Clock />
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 500,
                }}
              >
                {assessmentDetails?.testDuration} min
              </Typography>
            </Box>
          </Box>
        </>
      )}
      <Typography
        variant="body2"
        sx={{
          color: "#59566C",
          fontSize: "12px",
          lineHeight: "20px",
        }}
      >
        {assessmentDetails?.skills || ""}
      </Typography>
       </>  
       }



      <Snackbar
        open={isToastOpen}
        autoHideDuration={3000}
        onClose={() => setIsToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setIsToastOpen(false)}
          severity={!isToastSuccess ? "error" : "success"}
          variant="filled"
          sx={{
            width: '100%', zIndex: '10000', fontSize: '14px',
            backgroundColor: !isToastSuccess ? '#ff7f7f' : 'rgb(50 182 144)',
          }}
        >
          {toastText}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HeaderSection;
